import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Conversation, ConversationMode } from '../api'
import {
  Brain,
  Clock,
  Compose,
  Exit,
  Mark,
  Moon,
  More,
  PanelLeft,
  Pencil,
  Pin,
  PinFilled,
  Search,
  Sun,
  Tasks,
  Trash,
} from './icons'

interface Props {
  conversations: Conversation[]
  activeId: number | null
  /** Mobile drawer state. */
  open: boolean
  /** Collapsed to the icon rail on desktop. Separate from `open`. */
  collapsed: boolean
  onToggleCollapse: () => void
  userName: string
  dark: boolean
  /** Absent in anonymous mode — there is no session to end. */
  onLogout?: () => void
  onToggleTheme: () => void
  onClose: () => void
  onSelect: (id: number) => void
  onNew: (mode: ConversationMode) => void
  onRename: (id: number, title: string) => void
  onDelete: (id: number) => void
  onTogglePin: (id: number, pinned: boolean) => void
  onToggleMode: (id: number, mode: ConversationMode) => void
  /** Ходить ли в интернет. Настройка человека, а не треда. */
  webEnabled: boolean
  onToggleWeb: (enabled: boolean) => void
  /**
   * Показывать ли вход в фоновые задачи.
   *
   * Кнопки нет, пока она ничего не открывает: в обычном треде без единой
   * живой задачи открывать нечего, и постоянный ярлык там — это ярлык,
   * который человек научился не замечать.
   */
  tasksVisible: boolean
  /** Сколько задач ещё в игре. 0 — значка нет. */
  tasksCount: number
  tasksOpen: boolean
  onToggleTasks: (e: React.MouseEvent<HTMLElement>) => void
}

function IconButton({
  onClick,
  label,
  children,
}: {
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="rounded-md p-1 text-(--ink-faint) transition hover:bg-(--raised-hi) hover:text-(--ink)"
    >
      {children}
    </button>
  )
}

/**
 * Счётчик живых задач.
 *
 * Заливкой, а не цветом: в палитре нет ни одного оттенка, поэтому «обрати
 * внимание» здесь выражается инверсией — единственным, что ярче обычного
 * текста.
 */
function Badge({ count }: { count: number }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute -top-0.5 -right-0.5 flex min-w-3.5 items-center
                 justify-center rounded-full bg-(--inverse-bg) px-1 text-[0.6rem] font-medium
                 leading-[0.9rem] text-(--inverse)"
    >
      {count}
    </span>
  )
}

function Row({
  conv,
  active,
  onSelect,
  onRename,
  onDelete,
  onTogglePin,
  onToggleMode,
}: {
  conv: Conversation
  active: boolean
  onSelect: () => void
  onRename: (title: string) => void
  onDelete: () => void
  onTogglePin: () => void
  onToggleMode: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(conv.title)
  const [confirming, setConfirming] = useState(false)
  const [menuAt, setMenuAt] = useState<{ top: number; left: number } | null>(null)
  const menu = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!menuAt) return
    const close = () => setMenuAt(null)
    const outside = (e: PointerEvent) => {
      if (!menu.current?.contains(e.target as Node) && !trigger.current?.contains(e.target as Node)) close()
    }
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(); trigger.current?.focus() }
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('resize', close)
    }
  }, [menuAt])

  if (editing) {
    return (
      <div className="px-1 py-0.5">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const next = draft.trim()
              if (next) onRename(next)
              setEditing(false)
            }
            if (e.key === 'Escape') {
              setDraft(conv.title)
              setEditing(false)
            }
          }}
          className="w-full rounded-lg border border-(--hairline) bg-(--raised) px-2 py-1.5
                     text-base text-(--ink) outline-none focus:border-(--ink-faint) md:text-sm"
        />
      </div>
    )
  }

  // Deleting is irreversible from here, so the first press only asks.
  if (confirming) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-(--raised) px-2 py-1.5">
        <span className="min-w-0 flex-1 truncate text-sm text-(--ink-dim)">
          удалить?
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 text-xs text-(--ink) underline underline-offset-2"
        >
          да
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="shrink-0 text-xs text-(--ink-faint)"
        >
          нет
        </button>
      </div>
    )
  }

  return (
    <div
      className={`group flex items-center gap-1 rounded-xl px-3 transition ${
        active ? 'bg-(--raised-hi)' : 'hover:bg-(--raised)'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-2 py-2.5 text-left"
        title={conv.title}
        aria-current={active ? 'page' : undefined}
      >
        <span
          className={`truncate text-sm ${
            active ? 'text-(--ink)' : 'text-(--ink-dim)'
          }`}
        >
          {conv.title}
        </span>
        {/* Агентский тред помечен, потому что отличается он не видом, а
            правами: там есть песочница, файлы и фоновые задачи. Человек,
            который этого не видит, однажды напишет не в тот тред. */}
        {conv.mode === 'agent' && (
          <span
            className="shrink-0 rounded-md border border-(--hairline) px-1 text-[10px]
                       leading-4 text-(--ink-faint)"
            title="агентский режим: инструменты и фоновые задачи"
          >
            агент
          </span>
        )}
      </button>

      <button ref={trigger} type="button" aria-label={`Действия: ${conv.title}`}
        aria-expanded={menuAt !== null} className="rounded-lg p-1.5 text-(--ink-dim) hover:bg-(--raised-hi) md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          setMenuAt(menuAt ? null : { top: Math.max(8, Math.min(r.bottom + 4, window.innerHeight - 204)), left: Math.max(8, Math.min(r.left, window.innerWidth - 232)) })
        }}><More size={19} /></button>
      {menuAt && createPortal(<div ref={menu} role="group" aria-label="Действия диалога" style={menuAt} className="conversation-menu"
        onClick={() => setMenuAt(null)}>
        {/* Переключатель режима стоит первым: он меняет не вид треда, а то,
            что тред умеет, — и это единственное действие в строке, которое
            что-то включает, а не переставляет. */}
        <IconButton
          onClick={onToggleMode}
          label={
            conv.mode === 'agent'
              ? 'сделать обычным чатом'
              : 'включить режим агента'
          }
        >
          <Brain size={14} />
          {conv.mode === 'agent' ? 'Обычный чат' : 'Режим агента'}
        </IconButton>
        <IconButton
          onClick={onTogglePin}
          label={conv.pinned ? 'открепить' : 'закрепить'}
        >
          {conv.pinned ? <PinFilled size={14} /> : <Pin size={14} />}
          {conv.pinned ? 'Открепить' : 'Закрепить'}
        </IconButton>
        <IconButton
          onClick={() => {
            setDraft(conv.title)
            setEditing(true)
          }}
          label="переименовать"
        >
          <Pencil size={14} />
          Переименовать
        </IconButton>
        <IconButton onClick={() => setConfirming(true)} label="удалить">
          <Trash size={14} />
          Удалить
        </IconButton>
      </div>, document.body)}
    </div>
  )
}

/** One slot on the collapsed rail. */
function RailButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex size-9 items-center justify-center rounded-lg transition ${
        active
          ? 'bg-(--raised-hi) text-(--ink)'
          : 'text-(--ink-dim) hover:bg-(--raised) hover:text-(--ink)'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Panel that opens beside the rail.
 *
 * Positioned `fixed` rather than absolutely inside the rail: the sidebar now
 * clips its own contents (that is what lets the panel slide out of view as the
 * width shrinks), and an absolutely positioned child would be clipped with it.
 * `top` comes from the icon that opened it, so it still reads as anchored.
 */
function Flyout({
  title,
  top,
  children,
}: {
  title: string
  top: number
  children: React.ReactNode
}) {
  return (
    <div
      style={{ top }}
      className="flyout fixed left-16 z-30 max-h-[70vh] w-72 overflow-y-auto
                 rounded-xl border border-(--hairline) bg-(--surface) p-2 shadow-xl"
    >
      <p className="section-label px-2 pt-1 pb-1.5">{title}</p>
      {children}
    </div>
  )
}

type Panel = 'search' | 'pinned' | 'recents' | null

const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

/** Floating panel width (w-72) and the margin it keeps from the viewport edge. */
const PANEL_W = 288
const GUTTER = 8

/**
 * Slide rows to their new places instead of letting them teleport.
 *
 * Standard FLIP: remember where every row was, let React reorder the DOM, then
 * offset each moved row back to its old position and release it. Pinning is
 * the whole reason this exists — without it the row vanishes from one section
 * and reappears in another, and you lose track of what you just did.
 */
function useRowFlip(deps: unknown) {
  const rows = useRef(new Map<number, HTMLDivElement>())
  const previous = useRef(new Map<number, number>())

  useLayoutEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const next = new Map<number, number>()

    // Measure everything first: applying a transform mid-loop would shift the
    // rows still to be measured.
    rows.current.forEach((el, id) => next.set(id, el.getBoundingClientRect().top))

    if (!reduce) {
      next.forEach((top, id) => {
        const was = previous.current.get(id)
        const el = rows.current.get(id)
        if (!el || was === undefined || was === top) return
        el.style.transition = 'none'
        el.style.transform = `translateY(${was - top}px)`
        requestAnimationFrame(() => {
          el.style.transition = `transform 220ms ${EASE}`
          el.style.transform = ''
        })
      })
    }

    previous.current = next
  }, [deps])

  return (id: number) => (el: HTMLDivElement | null) => {
    if (el) rows.current.set(id, el)
    else rows.current.delete(id)
  }
}

export function Sidebar({
  conversations,
  activeId,
  open,
  collapsed,
  onToggleCollapse,
  userName,
  dark,
  onLogout,
  onToggleTheme,
  onClose,
  onSelect,
  onNew,
  onRename,
  onDelete,
  onTogglePin,
  onToggleMode,
  webEnabled,
  onToggleWeb,
  tasksVisible,
  tasksCount,
  tasksOpen,
  onToggleTasks,
}: Props) {
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [panel, setPanel] = useState<Panel>(null)
  // Where the icon that opened the flyout sits, so the panel lines up with it.
  const [anchor, setAnchor] = useState(0)
  const [searchAt, setSearchAt] = useState({ top: 0, left: 0 })
  const rail = useRef<HTMLDivElement>(null)
  const searchPanel = useRef<HTMLDivElement>(null)
  const searchToggle = useRef<HTMLSpanElement>(null)
  const flipRef = useRowFlip(conversations)

  // Titles are already in memory, so filtering here is instant and needs no
  // round trip. Searching message bodies would need the server — the titles
  // are what the list actually shows, so this matches what you can see.
  const needle = query.trim().toLowerCase()
  const shown = needle
    ? conversations.filter((c) => c.title.toLowerCase().includes(needle))
    : conversations

  const pinned = shown.filter((c) => c.pinned)
  const recent = shown.filter((c) => !c.pinned)

  const closeSearch = () => {
    setSearching(false)
    setQuery('')
  }

  // Expanding puts the full list back on screen, so a flyout of the same rows
  // would be showing them twice. Collapsing hides the panel the floating
  // search belongs to, and the rail carries its own.
  useEffect(() => {
    if (!collapsed) setPanel(null)
    else closeSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed])

  // Dismiss the floating search on an outside press. The toggle is excluded
  // deliberately: mousedown lands before click, so closing here would let the
  // button's own handler immediately reopen it.
  useEffect(() => {
    if (!searching) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (searchPanel.current?.contains(t) || searchToggle.current?.contains(t)) {
        return
      }
      closeSearch()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching])

  // A flyout is a transient surface: anything outside it, or Escape, dismisses.
  useEffect(() => {
    if (!panel) return
    const onDown = (e: MouseEvent) => {
      if (!rail.current?.contains(e.target as Node)) setPanel(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanel(null)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [panel])

  // `flip` is only wired for the expanded list: the flyout is transient, and
  // measuring rows in both places at once would fight over the same ids.
  const rows = (list: Conversation[], after?: () => void, flip = false) =>
    list.map((c) => (
      <div key={c.id} ref={flip ? flipRef(c.id) : undefined}>
        <Row
          conv={c}
          active={c.id === activeId}
          onSelect={() => {
            onSelect(c.id)
            after?.()
          }}
          onRename={(title) => onRename(c.id, title)}
          onDelete={() => onDelete(c.id)}
          onTogglePin={() => onTogglePin(c.id, !c.pinned)}
          onToggleMode={() =>
            onToggleMode(c.id, c.mode === 'agent' ? 'chat' : 'agent')
          }
        />
      </div>
    ))

  const togglePanel = (p: Panel, e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget.getBoundingClientRect().top)
    setPanel((prev) => (prev === p ? null : p))
  }

  return (
    <>
      {/* Kept mounted so it can fade rather than blink in and out. */}
      <div
        className={`fixed inset-0 z-10 bg-black/50 transition-opacity duration-200 md:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden
      />

      {/*
        Search floats outside the panel, hanging off the icon that opened it,
        instead of taking a row inside. Every in-flow position was wrong for a
        different reason: the header squeezed it to a sliver and evicted the
        wordmark, the «новый чат» row made starting a chat impossible
        mid-search, and its own row pushed the whole list down.

        It is rendered as a SIBLING of the panel, not a child, and that part is
        load-bearing. The drawer slides on a transform below `md`, and a
        transformed element becomes the containing block for its `fixed`
        descendants — so nested here it was both trapped inside the sidebar and
        clipped by its `overflow-hidden`, which is exactly how it came out cut
        in half on a phone. Desktop hid the bug: the transform is `max-md:`
        only, so there the panel escaped cleanly.
      */}
      {searching && (
        <div
          ref={searchPanel}
          style={searchAt}
          className="flyout fixed z-30 w-72 rounded-xl border border-(--hairline)
                     bg-(--surface) p-2 shadow-xl"
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch()
            }}
            placeholder="искать диалог"
            aria-label="искать диалог"
            className="w-full rounded-lg border border-(--hairline) bg-(--raised) px-2.5 py-2
                       text-base text-(--ink) outline-none placeholder:text-(--ink-faint)
                       focus:border-(--ink-faint) md:text-sm"
          />
        </div>
      )}

      {/*
        One element that changes width, not two that swap. Both layouts live
        inside it at their natural widths and cross-fade; `overflow-hidden`
        does the rest, so the expanded contents slide out of view as the panel
        narrows instead of reflowing at every intermediate width — text that
        re-wraps mid-animation is what makes this look cheap.

        Width is the thing animating, which means layout runs each frame. That
        is the honest cost of keeping the panel adjacent to the content; at one
        sidebar it is imperceptible.
      */}
      <aside
        className={`sidebar-surface relative flex shrink-0 flex-col overflow-hidden
                    max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-20 max-md:w-64
                    max-md:transition-transform max-md:duration-200 max-md:ease-out
                    md:transition-[width] md:duration-200 md:ease-out
                    ${open ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}
                    ${collapsed ? 'md:w-14' : 'md:w-64'}`}
      >
        {/*
          Expanded layout. The outgoing layer fades fast with no delay and the
          incoming one waits for it, so the two never overlap — their icons sit
          only a few pixels apart and a straight cross-fade reads as a double
          image.
        */}
        <div
          className={`absolute inset-y-0 left-0 flex w-64 flex-col transition-opacity ${
            collapsed
              ? 'md:pointer-events-none md:opacity-0 md:duration-100'
              : 'opacity-100 md:delay-100 md:duration-150'
          }`}
        >
          <div className="flex items-center gap-2 px-3 pt-4 pb-3">
            <Mark size={21} className="shrink-0 text-(--ink)" />
            <span className="flex-1 truncate font-semibold text-base text-(--ink)">
              Озарник
            </span>
            <span ref={searchToggle}>
              <IconButton
                onClick={(e) => {
                  if (searching) {
                    closeSearch()
                    return
                  }
                  const r = e.currentTarget.getBoundingClientRect()
                  // Keep the panel on screen: anchored at the icon it would
                  // hang off the right edge on a phone, where the drawer is
                  // nearly as wide as the viewport.
                  const max = window.innerWidth - PANEL_W - GUTTER
                  setSearchAt({
                    top: r.bottom + 8,
                    left: Math.max(GUTTER, Math.min(r.left, max)),
                  })
                  setSearching(true)
                }}
                label="поиск по диалогам"
              >
                <Search size={16} />
              </IconButton>
            </span>
            {/* Collapsing is a pointer-screen affordance; on mobile the panel
                is a drawer and the backdrop already closes it. */}
            <span className="hidden md:block">
              <IconButton onClick={onToggleCollapse} label="свернуть панель">
                <PanelLeft size={16} />
              </IconButton>
            </span>
          </div>

          {/* Два входа, а не переключатель внутри: режим решает, что тред
              вообще умеет, и выбирается он в начале — как «new chat» и
              «агент» слева у Zed. */}
          <div className="px-2.5 py-2">
            <button
              type="button"
              onClick={() => onNew('chat')}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-(--ink)
                         transition hover:bg-(--raised-hi)"
            >
              <Compose size={21} className="shrink-0 text-(--ink-dim)" />
              новый чат
            </button>
            <button
              type="button"
              onClick={() => onNew('agent')}
              className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-(--ink-dim)
                         transition hover:bg-(--raised) hover:text-(--ink)"
              title="инструменты, файлы, песочница и фоновые задачи"
            >
              <Brain size={17} className="shrink-0 text-(--ink-faint)" />
              агент
            </button>
          </div>

          <nav aria-label="История чатов" className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            <p className="section-label px-3 pt-6 pb-3">История чатов</p>
            {conversations.length === 0 && <p className="px-3 py-2 text-xs text-(--ink-faint)">Пока нет диалогов</p>}
            {conversations.length > 0 && shown.length === 0 && (
              <p className="px-2 py-3 text-xs text-(--ink-faint)">
                ничего не нашлось
              </p>
            )}

            {pinned.length > 0 && (
              <>
                <p className="section-label px-3 pt-4 pb-2">закреплённые</p>
                <div className="space-y-px">{rows(pinned, undefined, true)}</div>
              </>
            )}

            {recent.length > 0 && (
              <>
                {/* While filtering, «недавние» would be a lie — these are hits. */}
                <p className="section-label px-3 pt-4 pb-2">
                  {needle ? 'найденные' : 'недавние'}
                </p>
                <div className="space-y-px">{rows(recent, undefined, true)}</div>
              </>
            )}
          </nav>

          {/* Account and theme live down here so the conversation area needs no
              top bar at all — that reclaims a full row of vertical space. */}
          <div className="flex items-center gap-2 border-t border-(--hairline) px-3 py-3">
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded-full
                         bg-(--raised-hi) text-[0.65rem] font-medium text-(--ink-dim)"
              aria-hidden
            >
              {userName.trim().charAt(0).toLowerCase() || '?'}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-(--ink-dim)">
              {userName}
            </span>
            {/* Подпись говорит про состояние, а не про действие: человек
                должен видеть, ходит ли ассистент наружу, не нажимая. */}
            <IconButton
              onClick={() => onToggleWeb(!webEnabled)}
              label={webEnabled ? 'интернет включён' : 'интернет выключен'}
            >
              <Search
                size={16}
                className={webEnabled ? undefined : 'opacity-40'}
              />
            </IconButton>
            {tasksVisible && (
              <span data-tasks-toggle className="relative">
                <IconButton
                  onClick={onToggleTasks}
                  label={
                    tasksCount > 0
                      ? `фоновые задачи: ${tasksCount}`
                      : 'фоновые задачи'
                  }
                >
                  <Tasks size={16} className={tasksOpen ? 'text-(--ink)' : undefined} />
                </IconButton>
                {tasksCount > 0 && <Badge count={tasksCount} />}
              </span>
            )}
            <IconButton
              onClick={onToggleTheme}
              label={dark ? 'светлая тема' : 'тёмная тема'}
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </IconButton>
            {onLogout && (
              <IconButton onClick={onLogout} label="выйти">
                <Exit size={16} />
              </IconButton>
            )}
          </div>
        </div>

        {/*
          Collapsed layout. Collapsed does not mean gone: the rail keeps what
          you reach for without reading a list — new chat, search, pinned,
          recents — and the mark brings the panel back. Desktop only; on a
          phone the panel is a drawer and a permanent rail would eat the width
          it exists to save.
        */}
        <div
          ref={rail}
          className={`absolute inset-y-0 left-0 hidden w-14 flex-col items-center gap-1 py-3
                      transition-opacity md:flex ${
                        collapsed
                          ? 'opacity-100 delay-100 duration-150'
                          : 'pointer-events-none opacity-0 duration-100'
                      }`}
        >
          <button
            type="button"
            onClick={onToggleCollapse}
            title="развернуть панель"
            aria-label="развернуть панель"
            className="mb-3 flex size-9 items-center justify-center rounded-lg text-(--ink)
                       transition hover:bg-(--raised)"
          >
            <Mark size={20} />
          </button>

          <RailButton label="новый чат" onClick={() => onNew('chat')}>
            <Compose size={18} />
          </RailButton>

          <div>
            <RailButton
              label="поиск"
              active={panel === 'search'}
              onClick={(e) => togglePanel('search', e)}
            >
              <Search size={18} />
            </RailButton>
            {panel === 'search' && (
              <Flyout title="поиск" top={anchor}>
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setPanel(null)
                  }}
                  placeholder="искать диалог"
                  aria-label="искать диалог"
                  className="mb-1 w-full rounded-lg border border-(--hairline) bg-(--raised) px-2.5 py-2
                             text-base text-(--ink) outline-none placeholder:text-(--ink-faint)
                             focus:border-(--ink-faint) md:text-sm"
                />
                {shown.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-(--ink-faint)">
                    ничего не нашлось
                  </p>
                ) : (
                  <div className="space-y-px">{rows(shown, () => setPanel(null))}</div>
                )}
              </Flyout>
            )}
          </div>

          <div>
            <RailButton
              label="закреплённые диалоги"
              active={panel === 'pinned'}
              onClick={(e) => togglePanel('pinned', e)}
            >
              <Pin size={18} />
            </RailButton>
            {panel === 'pinned' && (
              <Flyout title="закреплённые" top={anchor}>
                {conversations.filter((c) => c.pinned).length === 0 ? (
                  <p className="px-2 py-2 text-xs text-(--ink-faint)">
                    ничего не закреплено
                  </p>
                ) : (
                  <div className="space-y-px">
                    {rows(
                      conversations.filter((c) => c.pinned),
                      () => setPanel(null),
                    )}
                  </div>
                )}
              </Flyout>
            )}
          </div>

          <div>
            <RailButton
              label="недавние диалоги"
              active={panel === 'recents'}
              onClick={(e) => togglePanel('recents', e)}
            >
              <Clock size={18} />
            </RailButton>
            {panel === 'recents' && (
              <Flyout title="недавние" top={anchor}>
                {conversations.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-(--ink-faint)">
                    здесь появятся диалоги
                  </p>
                ) : (
                  <div className="space-y-px">
                    {rows(conversations, () => setPanel(null))}
                  </div>
                )}
              </Flyout>
            )}
          </div>

          {/* Задачи и на свёрнутой колонке: живая задача — единственное, что
              здесь меняется само, и не показать её значит спрятать. */}
          {tasksVisible && (
            <span data-tasks-toggle className="relative">
              <RailButton
                label={
                  tasksCount > 0 ? `фоновые задачи: ${tasksCount}` : 'фоновые задачи'
                }
                active={tasksOpen}
                onClick={onToggleTasks}
              >
                <Tasks size={18} />
              </RailButton>
              {tasksCount > 0 && <Badge count={tasksCount} />}
            </span>
          )}
        </div>
      </aside>
    </>
  )
}
